import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  Copy,
  ExternalLink,
  Fingerprint,
  KeyRound,
  Link2,
  LockKeyhole,
  Pencil,
  Plus,
  RotateCw,
  ShieldCheck,
  ShieldOff,
  Trash2,
} from "lucide-react";
import { FormEvent, useState } from "react";

import {
  CurrentSession,
  ExternalIdentity,
  ExternalProvider,
  MfaStatus,
  PasskeyView,
  api,
  authenticatePasskey,
  formatDate,
  registerPasskey,
} from "../api";
import { EmptyState, ErrorState, LoadingState, MutationMessage, PageHeader, Section, StatusBadge } from "./components";

interface TotpEnrollment {
  id: string;
  secret: string;
  provisioningUri: string;
  algorithm: string;
  digits: number;
  periodSeconds: number;
  expiresAt: string;
}

interface RecoveryCodesResult {
  codes: string[];
  generation: number;
  generatedAt: string;
}

export function SecurityPage({ session }: { session: CurrentSession }) {
  const client = useQueryClient();
  const mfa = useQuery({ queryKey: ["mfa"], queryFn: () => api<MfaStatus>("/api/v1/mfa") });
  const refreshSecurity = () => {
    void client.invalidateQueries({ queryKey: ["mfa"] });
    void client.invalidateQueries({ queryKey: ["session"] });
  };

  return (
    <>
      <PageHeader
        eyebrow="SECURITY"
        title="登录与安全"
        description="管理密码、双重验证、Passkey 和外部身份。"
        action={<StatusBadge tone={mfa.data?.assurance.recent ? "good" : "warning"}>{mfa.data?.assurance.recent ? "近期已验证" : "需要近期验证"}</StatusBadge>}
      />
      {mfa.isPending && <LoadingState />}
      {mfa.error && <ErrorState error={mfa.error} retry={() => void mfa.refetch()} />}
      {mfa.data && <StepUpPanel status={mfa.data} onVerified={refreshSecurity} />}
      <PasswordSection onChanged={refreshSecurity} />
      {mfa.data && <TotpSection status={mfa.data} onChanged={refreshSecurity} />}
      <PasskeySection onChanged={refreshSecurity} />
      <ExternalIdentitySection />
      <Section title="当前登录" description="此次会话使用的身份验证上下文。">
        <dl className="detail-list">
          <div><dt>登录方式</dt><dd>{session.authenticationMethod}</dd></div>
          <div><dt>保证级别</dt><dd>AAL{session.assuranceLevel}</dd></div>
          <div><dt>登录时间</dt><dd>{formatDate(session.authenticatedAt)}</dd></div>
          <div><dt>最近验证</dt><dd>{formatDate(session.stepUpAt)}</dd></div>
        </dl>
      </Section>
    </>
  );
}

export function StepUpPanel({ status, onVerified, minimumLevel = 1 }: { status: MfaStatus; onVerified: () => void; minimumLevel?: 1 | 2 }) {
  const [method, setMethod] = useState<"password" | "totp" | "recovery-code">(
    minimumLevel === 2
      ? status.totpEnabled ? "totp" : status.recoveryCodeRemaining > 0 ? "recovery-code" : "password"
      : "password",
  );
  const [value, setValue] = useState("");
  const verify = useMutation({
    mutationFn: () => api(`/api/v1/mfa/step-up/${method}`, {
      method: "POST",
      body: JSON.stringify(method === "password" ? { password: value } : { code: value }),
    }),
    onSuccess: () => { setValue(""); onVerified(); },
  });
  const passkey = useMutation({ mutationFn: authenticatePasskey, onSuccess: onVerified });

  if (status.assurance.recent && status.assurance.level >= minimumLevel) {
    return (
      <div className="assurance-banner good">
        <ShieldCheck /><div><strong>敏感操作已解锁</strong><span>有效至 {formatDate(status.assurance.expiresAt)}</span></div>
      </div>
    );
  }
  return (
    <section className="assurance-panel">
      <div className="assurance-copy"><LockKeyhole /><div><strong>{minimumLevel === 2 ? "需要 AAL2 验证" : "先确认是你本人"}</strong><span>{minimumLevel === 2 ? "高风险操作只接受 TOTP、恢复码或 Passkey。" : "修改凭据和隐私设置前需要近期验证。"}</span></div></div>
      <div className="segmented-control compact" aria-label="验证方式">
        {minimumLevel === 1 && <button className={method === "password" ? "active" : ""} type="button" onClick={() => setMethod("password")}>密码</button>}
        {status.totpEnabled && <button className={method === "totp" ? "active" : ""} type="button" onClick={() => setMethod("totp")}>验证码</button>}
        {status.recoveryCodeRemaining > 0 && <button className={method === "recovery-code" ? "active" : ""} type="button" onClick={() => setMethod("recovery-code")}>恢复码</button>}
      </div>
      {(minimumLevel === 1 || status.totpEnabled || status.recoveryCodeRemaining > 0 || status.passkeyCount > 0) && <form className="inline-verification" onSubmit={(event) => { event.preventDefault(); verify.mutate(); }}>
        {(minimumLevel === 1 || status.totpEnabled || status.recoveryCodeRemaining > 0) && <><input required autoComplete={method === "password" ? "current-password" : "one-time-code"} type={method === "password" ? "password" : "text"} value={value} onChange={(event) => setValue(event.target.value)} placeholder={method === "password" ? "当前密码" : method === "totp" ? "6 位验证码" : "恢复码"} /><button className="primary-button" disabled={verify.isPending} type="submit">验证</button></>}
        {status.passkeyCount > 0 && <button className="icon-button" disabled={passkey.isPending} title="使用 Passkey 验证" aria-label="使用 Passkey 验证" type="button" onClick={() => passkey.mutate()}><Fingerprint /></button>}
      </form>}
      <MutationMessage error={verify.error ?? passkey.error} success={verify.isSuccess && "验证完成"} />
    </section>
  );
}

function PasswordSection({ onChanged }: { onChanged: () => void }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const change = useMutation({
    mutationFn: () => {
      if (newPassword !== confirmation) throw new Error("两次输入的新密码不一致。");
      return api<{ revokedSessions: number }>("/api/v1/account/password", {
        method: "PUT",
        body: JSON.stringify({ currentPassword, newPassword }),
      });
    },
    onSuccess: () => { setCurrentPassword(""); setNewPassword(""); setConfirmation(""); onChanged(); },
  });
  return (
    <Section title="密码" description="修改后会撤销当前会话之外的登录。">
      <form className="settings-form" onSubmit={(event) => { event.preventDefault(); change.mutate(); }}>
        <div className="field-grid three">
          <label><span>当前密码</span><input required maxLength={1024} type="password" autoComplete="current-password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} /></label>
          <label><span>新密码</span><input required minLength={12} maxLength={1024} type="password" autoComplete="new-password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} /></label>
          <label><span>确认新密码</span><input required minLength={12} maxLength={1024} type="password" autoComplete="new-password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} /></label>
        </div>
        <MutationMessage error={change.error} success={change.isSuccess && "密码已修改"} />
        <div className="form-actions"><button className="primary-button" disabled={change.isPending} type="submit"><KeyRound />修改密码</button></div>
      </form>
    </Section>
  );
}

function TotpSection({ status, onChanged }: { status: MfaStatus; onChanged: () => void }) {
  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const begin = useMutation({ mutationFn: () => api<TotpEnrollment>("/api/v1/mfa/totp/enrollments", { method: "POST" }), onSuccess: setEnrollment });
  const confirm = useMutation({
    mutationFn: () => api<RecoveryCodesResult>(`/api/v1/mfa/totp/enrollments/${enrollment?.id}/confirmation`, { method: "POST", body: JSON.stringify({ code }) }),
    onSuccess: (result) => { setEnrollment(null); setCode(""); setRecoveryCodes(result.codes); onChanged(); },
  });
  const rotate = useMutation({ mutationFn: () => api<RecoveryCodesResult>("/api/v1/mfa/recovery-codes/rotation", { method: "POST" }), onSuccess: (result) => { setRecoveryCodes(result.codes); onChanged(); } });
  const disable = useMutation({ mutationFn: () => api<void>("/api/v1/mfa/totp", { method: "DELETE" }), onSuccess: () => { setRecoveryCodes([]); onChanged(); } });

  return (
    <Section title="验证器与恢复码" description="TOTP 为账号提供第二重验证；恢复码只会显示一次。" action={<StatusBadge tone={status.totpEnabled ? "good" : "neutral"}>{status.totpEnabled ? "已启用" : "未启用"}</StatusBadge>}>
      <div className="metric-row">
        <div><span>恢复码</span><strong>{status.recoveryCodeRemaining} / {status.recoveryCodeTotal}</strong></div>
        <div><span>当前代次</span><strong>{status.recoveryCodeGeneration || "-"}</strong></div>
        <div><span>上次轮换</span><strong>{formatDate(status.recoveryCodesRotatedAt)}</strong></div>
      </div>
      {enrollment && (
        <div className="enrollment-panel">
          <strong>在验证器中添加账号</strong>
          <p>手动输入以下密钥，或用支持的验证器打开配置 URI。密钥只在这里显示一次。</p>
          <code>{enrollment.secret}</code>
          <a className="secondary-button" href={enrollment.provisioningUri}><ExternalLink />打开验证器</a>
          <form className="inline-verification" onSubmit={(event) => { event.preventDefault(); confirm.mutate(); }}>
            <input required pattern="[0-9]{6,8}" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(event) => setCode(event.target.value)} placeholder="输入验证码确认" />
            <button className="primary-button" disabled={confirm.isPending} type="submit"><Check />确认启用</button>
          </form>
          <MutationMessage error={confirm.error} />
        </div>
      )}
      {recoveryCodes.length > 0 && <RecoveryCodes codes={recoveryCodes} onDismiss={() => setRecoveryCodes([])} />}
      <MutationMessage error={begin.error ?? rotate.error ?? disable.error} success={disable.isSuccess && "验证器已停用"} />
      <div className="button-row">
        <button className="secondary-button" type="button" disabled={begin.isPending} onClick={() => begin.mutate()}><Plus />{status.totpEnabled ? "更换验证器" : "启用验证器"}</button>
        {status.totpEnabled && <button className="secondary-button" type="button" disabled={rotate.isPending} onClick={() => rotate.mutate()}><RotateCw />轮换恢复码</button>}
        {status.totpEnabled && <button className="danger-button" type="button" disabled={disable.isPending} onClick={() => { if (window.confirm("停用验证器并使所有恢复码失效？")) disable.mutate(); }}><ShieldOff />停用</button>}
      </div>
    </Section>
  );
}

function RecoveryCodes({ codes, onDismiss }: { codes: string[]; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="recovery-codes" role="status">
      <div><strong>立即保存恢复码</strong><span>离开后无法再次查看；新一批会使旧恢复码全部失效。</span></div>
      <div className="code-grid">{codes.map((code) => <code key={code}>{code}</code>)}</div>
      <div className="button-row">
        <button className="secondary-button" type="button" onClick={() => { void navigator.clipboard.writeText(codes.join("\n")); setCopied(true); }}><Copy />{copied ? "已复制" : "复制"}</button>
        <button className="primary-button" type="button" onClick={onDismiss}><Check />我已保存</button>
      </div>
    </div>
  );
}

function PasskeySection({ onChanged }: { onChanged: () => void }) {
  const client = useQueryClient();
  const passkeys = useQuery({ queryKey: ["passkeys"], queryFn: () => api<{ passkeys: PasskeyView[] }>("/api/v1/passkeys") });
  const [label, setLabel] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const refresh = () => { void client.invalidateQueries({ queryKey: ["passkeys"] }); onChanged(); };
  const add = useMutation({ mutationFn: () => registerPasskey(label || "我的 Passkey"), onSuccess: () => { setLabel(""); refresh(); } });
  const rename = useMutation({ mutationFn: () => api<PasskeyView>(`/api/v1/passkeys/${editing}`, { method: "PATCH", body: JSON.stringify({ label: editLabel }) }), onSuccess: () => { setEditing(null); refresh(); } });
  const remove = useMutation({ mutationFn: (id: string) => api<void>(`/api/v1/passkeys/${id}`, { method: "DELETE" }), onSuccess: refresh });

  return (
    <Section title="Passkey" description="用设备生物识别或安全密钥登录，无需传输密码。">
      {passkeys.isPending && <LoadingState />}
      {passkeys.error && <ErrorState error={passkeys.error} />}
      {passkeys.data?.passkeys.length === 0 && <EmptyState icon={Fingerprint} title="尚未添加 Passkey" detail="添加后可以用于登录和敏感操作验证。" />}
      <div className="item-list">
        {passkeys.data?.passkeys.map((passkey) => (
          <article className="list-item" key={passkey.id}>
            <span className="item-icon"><Fingerprint /></span>
            <div className="item-main">
              {editing === passkey.id ? <form className="rename-form" onSubmit={(event) => { event.preventDefault(); rename.mutate(); }}><input autoFocus required maxLength={80} value={editLabel} onChange={(event) => setEditLabel(event.target.value)} /><button className="icon-button" aria-label="保存名称" title="保存名称"><Check /></button></form> : <><strong>{passkey.label}</strong><span>{passkey.transports.join(" / ") || "平台验证器"} · 添加于 {formatDate(passkey.createdAt)}</span></>}
            </div>
            <div className="item-actions">
              <button className="icon-button" type="button" title="重命名" aria-label={`重命名 ${passkey.label}`} onClick={() => { setEditing(passkey.id); setEditLabel(passkey.label); }}><Pencil /></button>
              <button className="icon-button danger" type="button" title="移除" aria-label={`移除 ${passkey.label}`} onClick={() => { if (window.confirm("移除这个 Passkey？")) remove.mutate(passkey.id); }}><Trash2 /></button>
            </div>
          </article>
        ))}
      </div>
      <MutationMessage error={add.error ?? rename.error ?? remove.error} success={add.isSuccess && "Passkey 已添加"} />
      <form className="add-row" onSubmit={(event) => { event.preventDefault(); add.mutate(); }}><input maxLength={80} value={label} onChange={(event) => setLabel(event.target.value)} placeholder="Passkey 名称，例如办公电脑" /><button className="primary-button" disabled={add.isPending} type="submit"><Plus />添加 Passkey</button></form>
    </Section>
  );
}

function ExternalIdentitySection() {
  const client = useQueryClient();
  const identities = useQuery({ queryKey: ["external-identities"], queryFn: () => api<{ identities: ExternalIdentity[] }>("/api/v1/external-identities") });
  const providers = useQuery({ queryKey: ["external-providers"], queryFn: () => api<{ providers: ExternalProvider[] }>("/api/v1/external-auth/providers") });
  const unlink = useMutation({ mutationFn: (id: string) => api<void>(`/api/v1/external-identities/${id}`, { method: "DELETE" }), onSuccess: () => void client.invalidateQueries({ queryKey: ["external-identities"] }) });
  const link = useMutation({
    mutationFn: (provider: string) => api<{ authorizationUrl: string }>(`/api/v1/external-identities/${provider}/link-authorizations?returnTo=%2Faccount%2Fsecurity`, { method: "POST" }),
    onSuccess: (value) => window.location.assign(value.authorizationUrl),
  });
  const linked = new Set(identities.data?.identities.map((identity) => identity.provider));
  const names = Object.fromEntries(providers.data?.providers.map((provider) => [provider.id, provider.displayName]) ?? []);

  return (
    <Section title="外部身份" description="连接 Google、GitHub 或 Apple 身份，作为额外登录入口。">
      {(identities.isPending || providers.isPending) && <LoadingState />}
      {!identities.isPending && !providers.isPending && identities.data?.identities.length === 0 && providers.data?.providers.length === 0 && <EmptyState icon={Link2} title="暂无可连接的外部身份" detail="超级管理员尚未启用 Google、GitHub 或 Apple 登录提供商。" />}
      <div className="item-list">
        {identities.data?.identities.map((identity) => (
          <article className="list-item" key={identity.id}><span className="item-icon"><Link2 /></span><div className="item-main"><strong>{names[identity.provider] ?? identity.provider}</strong><span>连接于 {formatDate(identity.linkedAt)} · 最近使用 {formatDate(identity.lastUsedAt)}</span></div><button className="danger-button" type="button" onClick={() => { if (window.confirm("解除此外部身份？请确认仍保留其他登录方式。")) unlink.mutate(identity.id); }}>解除连接</button></article>
        ))}
      </div>
      <MutationMessage error={identities.error ?? providers.error ?? unlink.error ?? link.error} success={unlink.isSuccess && "外部身份已解除"} />
      <div className="button-row">
        {providers.data?.providers.filter((provider) => !linked.has(provider.id)).map((provider) => <button className="secondary-button" disabled={link.isPending} key={provider.id} type="button" onClick={() => link.mutate(provider.id)}><Link2 />连接 {provider.displayName}</button>)}
      </div>
    </Section>
  );
}
